/**
 * Workspace store (R7/R8/R9) — the window state machine. Framework-free
 * (vanilla reducer + subscriptions; React binds via useSyncExternalStore
 * in useWorkspace.ts) per repo convention: hooks/context only, no store
 * library. Records carry a stable numeric id so React identity survives
 * in-window navigation; the one-place rule is enforced on the path index.
 *
 * One-place rule:
 * - a route lives in at most one place (floor or exactly one window)
 * - pop-out moves the floor route into a window, floor returns Home
 * - floor navigation to a windowed path ABSORBS the window (it closes;
 *   the floor now shows it)
 * - spawn-open on an already-windowed path focuses the existing window
 * - in-window navigation re-keys the record, arbitrating collisions:
 *   sibling holds target → close source + focus sibling; floor holds
 *   target → close source.
 */

export type DockZone = 'left-half' | 'right-half' | 'left-third' | 'center-third' | 'right-third'
export type WindowState = 'floating' | DockZone

export interface Size {
  w: number
  h: number
}

export interface Rect {
  x: number
  y: number
  w: number
  h: number
}

export interface WindowRecord {
  id: number
  path: string
  rect: Rect
  state: WindowState
  z: number
  hibernated: boolean
  livePty: boolean
  min: Size
}

export interface Viewport {
  width: number
  pointerFine: boolean
}

export interface WorkspaceState {
  windows: WindowRecord[]
  floorPath: string
  viewport: Viewport
}

/** Workspace exists only on desktop: ≥1024px AND a fine pointer (R9). */
export const DESKTOP_MIN_WIDTH = 1024
/** Thirds are offered at ultrawide widths only (AE6). */
export const ULTRAWIDE_MIN_WIDTH = 2560
/** Edge band (px) that resolves a drag-end into a dock zone. */
export const SNAP_EDGE_BAND = 40

/** Provisional per-surface minimum sizes — validated at the U9 gate. */
export function minSizeFor(path: string): Size {
  if (path.startsWith('/hermes/terminal')) return { w: 640, h: 400 }
  if (path === '/' || path === '/status' || path.startsWith('/status/')) return { w: 560, h: 400 }
  if (path.startsWith('/mail') || path.startsWith('/inbox') || path === '/hermes') return { w: 480, h: 360 }
  return { w: 400, h: 300 }
}

/**
 * Resolve a drag-end pointer position into a dock zone (or null to stay
 * floating). Side edges give halves; the top edge gives thirds on
 * ultrawide viewports and falls back to halves by side below that.
 * Corners are deterministic: side edges win.
 */
export function resolveSnapZone(px: number, py: number, vw: number, vh: number): DockZone | null {
  void vh
  if (px <= SNAP_EDGE_BAND) return 'left-half'
  if (px >= vw - SNAP_EDGE_BAND) return 'right-half'
  if (py <= SNAP_EDGE_BAND) {
    if (vw >= ULTRAWIDE_MIN_WIDTH) {
      if (px < vw / 3) return 'left-third'
      if (px < (vw * 2) / 3) return 'center-third'
      return 'right-third'
    }
    return px < vw / 2 ? 'left-half' : 'right-half'
  }
  return null
}

/**
 * Floor reservations as viewport-width fractions — the shell turns these
 * into CSS vars so the floor genuinely reflows (a true split, not
 * occlusion). A lone center-third reserves the left two thirds; the
 * floor lives in the remaining right third (the empty band shows the
 * void — deliberate).
 */
export function dockReservations(state: WorkspaceState): { left: number; right: number } {
  let left = 0
  let right = 0
  for (const w of state.windows) {
    if (w.hibernated) continue
    if (w.state === 'left-half') left = Math.max(left, 0.5)
    else if (w.state === 'left-third') left = Math.max(left, 1 / 3)
    else if (w.state === 'center-third') left = Math.max(left, 2 / 3)
    else if (w.state === 'right-half') right = Math.max(right, 0.5)
    else if (w.state === 'right-third') right = Math.max(right, 1 / 3)
  }
  return { left, right }
}

/** Dock-band rect as viewport fractions (WindowFrame positions from this). */
export function zoneFractions(zone: DockZone): { x: number; w: number } {
  switch (zone) {
    case 'left-half':
      return { x: 0, w: 0.5 }
    case 'right-half':
      return { x: 0.5, w: 0.5 }
    case 'left-third':
      return { x: 0, w: 1 / 3 }
    case 'center-third':
      return { x: 1 / 3, w: 1 / 3 }
    case 'right-third':
      return { x: 2 / 3, w: 1 / 3 }
  }
}

const VALID_STATES: ReadonlySet<string> = new Set([
  'floating',
  'left-half',
  'right-half',
  'left-third',
  'center-third',
  'right-third',
])

interface OpenOptions {
  minSize?: Size
}

export interface CloseResult {
  needsConfirm: boolean
}

export type WorkspaceStore = ReturnType<typeof createWorkspaceStore>

export function createWorkspaceStore(initial?: { viewport?: Viewport; floorPath?: string }) {
  let state: WorkspaceState = {
    windows: [],
    floorPath: initial?.floorPath ?? '/',
    viewport: initial?.viewport ?? { width: 1600, pointerFine: true },
  }
  let nextId = 1
  let zCounter = 1
  const listeners = new Set<() => void>()

  function set(next: WorkspaceState) {
    state = next
    listeners.forEach((l) => l())
  }

  function belowBoundary(v: Viewport): boolean {
    return v.width < DESKTOP_MIN_WIDTH || !v.pointerFine
  }

  function cascadeRect(count: number, min: Size): Rect {
    return {
      x: 120 + (count % 8) * 32,
      y: 90 + (count % 8) * 26,
      w: Math.max(640, min.w),
      h: Math.max(420, min.h),
    }
  }

  /** Thirds clamp (AE6): below ultrawide, or when the window's minimum
   *  doesn't fit a third, a third dock becomes the matching half. */
  function effectiveZone(zone: DockZone, min: Size, viewport: Viewport): DockZone {
    if (zone !== 'left-third' && zone !== 'center-third' && zone !== 'right-third') return zone
    const thirdFits = viewport.width >= ULTRAWIDE_MIN_WIDTH && min.w <= viewport.width / 3
    if (thirdFits) return zone
    return zone === 'right-third' ? 'right-half' : 'left-half'
  }

  function withFocus(windows: WindowRecord[], id: number): WindowRecord[] {
    zCounter += 1
    return windows.map((w) => (w.id === id ? { ...w, z: zCounter } : w))
  }

  function removeWindow(windows: WindowRecord[], id: number): WindowRecord[] {
    return windows.filter((w) => w.id !== id)
  }

  function createRecord(path: string, opts?: OpenOptions): WindowRecord {
    const min = opts?.minSize ?? minSizeFor(path)
    zCounter += 1
    return {
      id: nextId++,
      path,
      rect: cascadeRect(state.windows.length, min),
      state: 'floating',
      z: zCounter,
      hibernated: belowBoundary(state.viewport),
      livePty: false,
      min,
    }
  }

  function dockInto(windows: WindowRecord[], id: number, zone: DockZone): WindowRecord[] {
    // One window per dock zone: the previous occupant returns to floating.
    return windows.map((w) => {
      if (w.id === id) return { ...w, state: zone }
      if (w.state === zone) return { ...w, state: 'floating' as WindowState }
      return w
    })
  }

  return {
    getState: () => state,

    subscribe(listener: () => void): () => void {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },

    /** The floor navigated. Absorb any window holding that path. */
    floorNavigated(path: string) {
      const held = state.windows.find((w) => w.path === path)
      // No-op when nothing changes — every route render calls this, and a
      // gratuitous state swap re-renders every workspace subscriber.
      if (!held && state.floorPath === path) return
      set({
        ...state,
        floorPath: path,
        windows: held ? removeWindow(state.windows, held.id) : state.windows,
      })
    },

    /** Pop the current floor route out into a window; floor goes Home. */
    popOut(path: string) {
      if (path === '/') return // floor returns Home — Home can't also be a window
      const existing = state.windows.find((w) => w.path === path)
      if (existing) {
        set({ ...state, floorPath: '/', windows: withFocus(state.windows, existing.id) })
        return
      }
      set({ ...state, floorPath: '/', windows: [...state.windows, createRecord(path)] })
    },

    /** Spawn affordance: open a path as a window (focus if already open). */
    openWindow(path: string, opts?: OpenOptions) {
      const existing = state.windows.find((w) => w.path === path)
      if (existing) {
        set({ ...state, windows: withFocus(state.windows, existing.id) })
        return
      }
      if (state.floorPath === path && path !== '/') {
        // The floor holds it: pop-out semantics (floor → Home).
        set({ ...state, floorPath: '/', windows: [...state.windows, createRecord(path, opts)] })
        return
      }
      set({ ...state, windows: [...state.windows, createRecord(path, opts)] })
    },

    closeWindow(id: number): CloseResult {
      const w = state.windows.find((x) => x.id === id)
      if (!w) return { needsConfirm: false }
      if (w.livePty) return { needsConfirm: true }
      set({ ...state, windows: removeWindow(state.windows, id) })
      return { needsConfirm: false }
    },

    confirmCloseWindow(id: number) {
      set({ ...state, windows: removeWindow(state.windows, id) })
    },

    closeAll(): CloseResult {
      if (state.windows.some((w) => w.livePty)) return { needsConfirm: true }
      set({ ...state, windows: [] })
      return { needsConfirm: false }
    },

    confirmCloseAll() {
      set({ ...state, windows: [] })
    },

    focusWindow(id: number) {
      set({ ...state, windows: withFocus(state.windows, id) })
    },

    moveWindow(id: number, rect: Rect) {
      set({
        ...state,
        windows: state.windows.map((w) =>
          w.id === id
            ? { ...w, rect: { ...rect, w: Math.max(rect.w, w.min.w), h: Math.max(rect.h, w.min.h) } }
            : w,
        ),
      })
    },

    resizeWindow(id: number, rect: Rect) {
      this.moveWindow(id, rect)
    },

    dockWindow(id: number, zone: DockZone) {
      const w = state.windows.find((x) => x.id === id)
      if (!w) return
      const z = effectiveZone(zone, w.min, state.viewport)
      set({ ...state, windows: withFocus(dockInto(state.windows, id, z), id) })
    },

    undockWindow(id: number) {
      set({
        ...state,
        windows: state.windows.map((w) => (w.id === id ? { ...w, state: 'floating' as WindowState } : w)),
      })
    },

    /**
     * Viewport changed. Below the desktop boundary all windows hibernate
     * (layout retained; live-PTY windows stay mounted-hidden — the
     * rendering layer keeps them in the DOM). Shrinking below ultrawide
     * remaps third docks to halves (AE6).
     */
    setViewport(width: number, pointerFine: boolean) {
      if (state.viewport.width === width && state.viewport.pointerFine === pointerFine) return
      const viewport = { width, pointerFine }
      const hibernate = belowBoundary(viewport)
      let windows = state.windows.map((w) => ({ ...w, hibernated: hibernate }))
      if (width < ULTRAWIDE_MIN_WIDTH) {
        for (const w of windows) {
          if (w.state === 'left-third' || w.state === 'center-third' || w.state === 'right-third') {
            const half: DockZone = w.state === 'right-third' ? 'right-half' : 'left-half'
            windows = dockInto(windows, w.id, half)
          }
        }
      }
      set({ ...state, viewport, windows })
    },

    setPtyLive(id: number, live: boolean) {
      set({
        ...state,
        windows: state.windows.map((w) => (w.id === id ? { ...w, livePty: live } : w)),
      })
    },

    /**
     * In-window navigation: re-key the record, arbitrating one-place
     * collisions. Same path → no-op. Sibling holds target → close the
     * navigating window, focus the sibling. Floor holds target → close
     * the navigating window. Otherwise re-key in place (stable id; pty
     * flag resets — the content changed).
     */
    windowNavigated(id: number, newPath: string) {
      const source = state.windows.find((w) => w.id === id)
      if (!source || source.path === newPath) return
      const sibling = state.windows.find((w) => w.path === newPath && w.id !== id)
      if (sibling) {
        set({ ...state, windows: withFocus(removeWindow(state.windows, id), sibling.id) })
        return
      }
      if (state.floorPath === newPath) {
        set({ ...state, windows: removeWindow(state.windows, id) })
        return
      }
      set({
        ...state,
        windows: state.windows.map((w) =>
          w.id === id ? { ...w, path: newPath, livePty: false, min: minSizeFor(newPath) } : w,
        ),
      })
    },

    /** Persisted layout (v2:workspace). Restores are cold mounts. */
    serialize(): string {
      return JSON.stringify({
        version: 1,
        windows: state.windows.map((w) => ({ path: w.path, rect: w.rect, state: w.state, z: w.z })),
      })
    },

    hydrate(json: string) {
      let data: unknown
      try {
        data = JSON.parse(json)
      } catch {
        return
      }
      if (typeof data !== 'object' || data === null) return
      const d = data as { version?: unknown; windows?: unknown }
      if (d.version !== 1 || !Array.isArray(d.windows)) return
      const records: WindowRecord[] = []
      for (const raw of d.windows) {
        if (typeof raw !== 'object' || raw === null) return
        const r = raw as { path?: unknown; rect?: unknown; state?: unknown; z?: unknown }
        if (typeof r.path !== 'string' || typeof r.z !== 'number') return
        if (typeof r.state !== 'string' || !VALID_STATES.has(r.state)) return
        const rect = r.rect as Rect | undefined
        if (
          !rect ||
          typeof rect.x !== 'number' ||
          typeof rect.y !== 'number' ||
          typeof rect.w !== 'number' ||
          typeof rect.h !== 'number'
        )
          return
        records.push({
          id: nextId++,
          path: r.path,
          rect: { x: rect.x, y: rect.y, w: rect.w, h: rect.h },
          state: r.state as WindowState,
          z: r.z,
          hibernated: belowBoundary(state.viewport),
          livePty: false, // cold restore — never resurrect a pty flag
          min: minSizeFor(r.path),
        })
      }
      zCounter = Math.max(zCounter, ...records.map((r) => r.z), 1)
      set({ ...state, windows: records })
    },
  }
}

/** App-wide singleton (React binds in useWorkspace.ts). */
export const workspaceStore = createWorkspaceStore()
